import { useLazyQuery } from '@apollo/client';
import debounce from 'debounce';
import React, { useEffect, useRef, useState } from 'react';

import { Icon, IconClose } from '@flixbus/honeycomb-icons-react';
import { Button, Input } from '@flixbus/honeycomb-react';

import { SEARCH_WORK_ASSIGNMENT_CANDIDATES } from 'pages/RideDetails/gql/searchWorkAssignmentCandidates';
import { sortCrewMembersByIdleTime } from 'utils/driverIdleSorter';

import { CREW_MEMBER_SEARCH_TEXT } from './texts';

import styles from './styles.module.css';

/**
 * @typedef {object} CrewMemberFilterProps
 * @property {boolean} isInSearchMode
 * @property {() => void} closeSearchMode
 * @property {() => void} startSearchMode
 * @property {(candidates: any[]) => void} updateExternalData
 * @property {(loading: boolean) => void} setLoading
 * @property {object} eligibleTo — extra Apollo variables spread into `searchFilter`.
 */

/** @param {CrewMemberFilterProps} props */
const CrewMemberFilter = ({
	isInSearchMode,
	closeSearchMode,
	startSearchMode,
	updateExternalData,
	setLoading,
	eligibleTo,
}) => {
	const [getCrewMembers, { data, loading, error }] = useLazyQuery(
		SEARCH_WORK_ASSIGNMENT_CANDIDATES,
		{
			fetchPolicy: 'no-cache',
			notifyOnNetworkStatusChange: true,
			onCompleted: (data) => {
				const sortedData = sortCrewMembersByIdleTime(data.searchWorkAssignmentCandidates);
				updateExternalData(sortedData);
			},
		}
	);

	const [localValue, setLocalValue] = useState('');
	const [isPristine, setIsPristine] = useState(true);
	const debounceRef = useRef(null);

	useEffect(() => {
		debounceRef.current = debounce((variables) => {
			getCrewMembers({ variables });
		}, 300);

		return () => {
			if (debounceRef.current) {
				debounceRef.current.clear();
			}
		};
	}, [getCrewMembers]);

	const handleOnChange = (e) => {
		if (isPristine) {
			setIsPristine(false);
		}
		setLocalValue(e.target.value);
		if (e.target.value === '') {
			updateExternalData([]);
			return;
		}

		if (debounceRef.current) {
			debounceRef.current({ searchFilter: { text: e.target.value }, ...eligibleTo });
		}
	};

	const handleClose = () => {
		setLocalValue('');
		closeSearchMode();
	};

	// Update the loading state to prevent memory leak
	useEffect(() => {
		if (loading) {
			setLoading(true);
		} else if (data || error) {
			setLoading(false);
		}
	}, [loading, data, error, setLoading]);

	return (
		<Input
			id={CREW_MEMBER_SEARCH_TEXT.inputId}
			label={CREW_MEMBER_SEARCH_TEXT.inputLabel}
			placeholder={CREW_MEMBER_SEARCH_TEXT.inputPlaceholder}
			onFocus={startSearchMode}
			onChange={(e) => {
				handleOnChange(e);
			}}
			value={localValue}
			iconRight={
				isInSearchMode && (
					<Button aria-label={CREW_MEMBER_SEARCH_TEXT.closeAriaLabel} onClick={handleClose}>
						<Icon extraClasses={styles.centerIcon} InlineIcon={IconClose} />
					</Button>
				)
			}
		/>
	);
};

export default CrewMemberFilter;
